// 게시 전 슬라이드 정리 — 끌어서 순서 바꾸기, 한 장 빼기.
//
// 캐러셀은 첫 장이 표지고 나머지 순서가 곧 넘겨보는 순서라, 게시 직전에 손볼 수 있어야 한다.
import { useCallback, useRef, useState } from "react";
import { GripVertical, LoaderCircle, X } from "lucide-react";
import type { FoodAsset, FoodPost } from "../shared/ootd-review";

export function FoodSlideStrip({ post, editable, onChanged, onError }: {
  post: FoodPost;
  editable: boolean;
  onChanged: (post: FoodPost) => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  // 드래그 중에는 서버를 건드리지 않는다. 놓을 때 한 번만 저장한다.
  const [draft, setDraft] = useState<FoodAsset[] | null>(null);
  const dragId = useRef<string | null>(null);

  const slides = draft ?? post.assets;

  const call = useCallback(async (url: string, init: RequestInit, label: string) => {
    setBusy(label);
    try {
      const res = await fetch(url, { credentials: "same-origin", ...init });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        throw new Error(`${body.error ?? "실패했어요"} (HTTP ${res.status}${body.code ? " · " + body.code : ""})`);
      }
      if (body.post) onChanged(body.post as FoodPost);
      setDraft(null);
      return true;
    } catch (error) {
      setDraft(null);
      onError(error instanceof Error ? error.message : String(error));
      return false;
    } finally {
      setBusy(null);
    }
  }, [onChanged, onError]);

  const commitOrder = (ordered: FoodAsset[]) => {
    if (ordered.every((a, i) => a.id === post.assets[i]?.id)) { setDraft(null); return; }
    setDraft(ordered);
    void call(`/api/food/${encodeURIComponent(post.id)}/slides/order`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids: ordered.map((a) => a.id) }),
    }, "order");
  };

  const move = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const next = [...slides];
    const from = next.findIndex((a) => a.id === fromId);
    const to = next.findIndex((a) => a.id === toId);
    if (from < 0 || to < 0) return;
    next.splice(to, 0, next.splice(from, 1)[0]);
    setDraft(next);
  };

  const remove = (asset: FoodAsset) => {
    if (!window.confirm(`${asset.position}번 "${asset.label}" 슬라이드를 뺄까요? 되돌릴 수 없어요.`)) return;
    void call(`/api/food/${encodeURIComponent(post.id)}/slides/${encodeURIComponent(asset.id)}`,
      { method: "DELETE" }, `del:${asset.id}`);
  };

  return (
    <div className="food-strip" aria-label={`${post.title} 슬라이드 ${slides.length}장`}>
      {slides.map((asset, index) => (
        <figure
          key={asset.id}
          className={`${dragId.current === asset.id ? "dragging" : ""} ${busy === "order" ? "saving" : ""}`}
          draggable={editable}
          onDragStart={(e) => { dragId.current = asset.id; e.dataTransfer.effectAllowed = "move"; }}
          onDragOver={(e) => { if (editable && dragId.current) { e.preventDefault(); move(dragId.current, asset.id); } }}
          onDragEnd={() => { const id = dragId.current; dragId.current = null; if (id) commitOrder(draft ?? slides); }}
          onDrop={(e) => e.preventDefault()}
        >
          <img src={asset.url} alt={asset.label} loading="lazy" />
          {editable && (
            <>
              <span className="slide-grip" title="끌어서 순서 변경"><GripVertical size={14} /></span>
              <button
                className="slide-remove"
                title="이 슬라이드 빼기"
                disabled={busy !== null || slides.length <= 2}
                onClick={() => remove(asset)}
              >
                {busy === `del:${asset.id}` ? <LoaderCircle className="spin" size={12} /> : <X size={13} />}
              </button>
            </>
          )}
          <figcaption>
            <b>{index + 1}. {asset.label}</b>
            {asset.sublabel && <span>{asset.sublabel}</span>}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
